import { useCallback, useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { api, fmtDuration } from "../lib/api";

const StatCard = ({ label, value, bg }) => (
  <div className="card-tactile p-4" style={{ background: bg }}>
    <p className="sd-label">{label}</p>
    <p className="font-heading font-extrabold text-3xl mt-1">{value}</p>
  </div>
);

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [range, setRange] = useState({ start: "", end: "" });

  const load = useCallback(() => {
    api.get("/dashboard/stats", { params: range }).then((r) => setStats(r.data));
  }, [range]);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const preset = (days) => {
    if (days === null) { setRange({ start: "", end: "" }); return; }
    const fmt = (d) => d.toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
    const endD = new Date();
    const startD = new Date();
    startD.setDate(endD.getDate() - days + 1);
    setRange({ start: fmt(startD), end: fmt(endD) });
  };

  if (!stats) return <div className="p-8 font-heading font-bold">Loading dashboard…</div>;

  return (
    <div className="p-6 overflow-y-auto h-full space-y-6" data-testid="dashboard-page">
      <div>
        <h2 className="font-heading font-extrabold text-2xl">Dashboard</h2>
        <p className="font-script text-xl text-amber-800">how's the team doing today? ✦</p>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard label="Chats Today" value={stats.today_chats} bg="var(--sd-sticky)" />
        <StatCard label="Open" value={stats.open} bg="var(--sd-green)" />
        <StatCard label="Resolved" value={stats.resolved} bg="var(--sd-blue)" />
        <StatCard label="Avg Resolution" value={fmtDuration(stats.avg_resolution_seconds)} bg="var(--sd-pink)" />
        <div className="card-tactile p-4" style={{ background: stats.sla_breached > 0 ? "#FECACA" : "var(--sd-green)" }} data-testid="sla-breached-card">
          <p className="sd-label">SLA Breached (48h)</p>
          <p className={`font-heading font-extrabold text-3xl mt-1 ${stats.sla_breached > 0 ? "text-red-700" : ""}`}>{stats.sla_breached}</p>
          <div className="flex gap-2">
            {stats.sla_at_risk > 0 && <p className="text-[11px] font-semibold text-amber-700 mt-0.5" data-testid="sla-at-risk-note">⏳ {stats.sla_at_risk} at risk (36h+)</p>}
            {stats.sla_unassigned > 0 && <p className="text-[11px] font-semibold text-red-700 mt-0.5" data-testid="sla-unassigned-note">{stats.sla_unassigned} unassigned</p>}
          </div>
        </div>
      </div>
      <div className="card-tactile p-4">
        <p className="sd-label mb-3">Chats per day (last 14 days)</p>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={stats.per_day}>
            <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(d) => d.slice(5)} />
            <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
            <Tooltip />
            <Legend />
            <Bar dataKey="created" name="New chats" fill="#131E3A" radius={[4, 4, 0, 0]} />
            <Bar dataKey="resolved" name="Resolved" fill="#7FB5A0" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="card-tactile p-4">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <p className="sd-label">Agent performance {range.start ? `· ${range.start} → ${range.end || "today"}` : "· all time"}</p>
          <div className="flex items-center gap-1.5 flex-wrap">
            <button data-testid="perf-preset-today" className={`filter-pill ${range.start && range.start === range.end ? "active" : ""}`} onClick={() => preset(1)}>Today</button>
            <button data-testid="perf-preset-7d" className="filter-pill" onClick={() => preset(7)}>7d</button>
            <button data-testid="perf-preset-30d" className="filter-pill" onClick={() => preset(30)}>30d</button>
            <button data-testid="perf-preset-all" className={`filter-pill ${!range.start && !range.end ? "active" : ""}`} onClick={() => preset(null)}>All</button>
            <input data-testid="perf-start-date" type="date" className="sd-input py-1 text-xs" style={{ width: "9.5rem" }} value={range.start} onChange={(e) => setRange((r) => ({ ...r, start: e.target.value }))} />
            <span className="text-xs font-bold">→</span>
            <input data-testid="perf-end-date" type="date" className="sd-input py-1 text-xs" style={{ width: "9.5rem" }} value={range.end} onChange={(e) => setRange((r) => ({ ...r, end: e.target.value }))} />
          </div>
        </div>
        <table className="w-full text-sm" data-testid="agent-performance-table">
          <thead>
            <tr className="text-left border-b-2 border-[#131E3A] font-heading text-xs uppercase tracking-wider">
              <th className="py-2">Agent</th><th>Status</th><th>Chats handled</th><th>Replies</th><th>Resolved</th><th>Avg resolution</th><th>CSAT</th><th>QA</th><th>SLA ⚠</th><th>Brownie</th>
            </tr>
          </thead>
          <tbody>
            {stats.agents.map((a) => (
              <tr key={a.id} className="border-b border-dashed border-slate-300">
                <td className="py-2 font-semibold">{a.name} <span className="text-[10px] uppercase text-slate-500">{a.role}</span></td>
                <td>{a.availability === "offline" ? <span className="text-amber-600 font-bold text-xs">⏸ on break</span> : a.online ? <span className="text-green-700 font-bold text-xs">● online</span> : <span className="text-slate-400 text-xs">○ offline</span>}</td>
                <td>{a.chats_handled}</td>
                <td>{a.replies}</td>
                <td>{a.resolved}</td>
                <td>{fmtDuration(a.avg_resolution_seconds)}</td>
                <td>{a.avg_csat ? <span className="font-semibold text-amber-600">★ {a.avg_csat} <span className="text-slate-400 text-xs">({a.csat_count})</span></span> : "—"}</td>
                <td data-testid={`qa-${a.id}`}>{a.avg_qa ? <span className="font-semibold text-purple-700">★ {a.avg_qa} <span className="text-slate-400 text-xs">({a.qa_count})</span></span> : "—"}</td>
                <td data-testid={`sla-${a.id}`}>{a.sla_breached > 0 ? <span className="font-bold text-red-700 bg-red-100 border border-red-600 rounded px-1.5 py-0.5 text-xs">{a.sla_breached}</span> : <span className="text-slate-400">0</span>}</td>
                <td data-testid={`brownie-${a.id}`}>{a.csat_count > 0 ? <span className={`font-heading font-bold ${a.brownie_points < 0 ? "text-red-600" : "text-green-700"}`}>{a.brownie_points > 0 ? `+${a.brownie_points}` : a.brownie_points}</span> : "—"}</td>
              </tr>
            ))}
            <tr className="border-t-2 border-[#131E3A] bg-[#FDF0A0]/60 font-heading font-bold" data-testid="perf-totals-row">
              <td className="py-2">Total</td>
              <td></td>
              <td>{stats.agents.reduce((s, a) => s + a.chats_handled, 0)}</td>
              <td>{stats.agents.reduce((s, a) => s + a.replies, 0)}</td>
              <td>{stats.agents.reduce((s, a) => s + a.resolved, 0)}</td>
              <td></td>
              <td></td>
              <td></td>
              <td data-testid="sla-agent-total" className="text-red-700 font-bold">{stats.agents.reduce((s, a) => s + (a.sla_breached || 0), 0)}</td>
              <td data-testid="brownie-total" className={stats.brownie_total < 0 ? "text-red-600" : "text-green-700"}>{stats.brownie_total > 0 ? `+${stats.brownie_total}` : stats.brownie_total}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
